import * as Yup from 'yup'
import FormBuilder, { FormField, FormFields } from "app/modules/tp/builders/FormBuilder";
import { Ride } from "../_models";
import ProviderAPI from "../../providers/_requests";
import ReferentAPI from "../../referents/_requests";
import MeanAPI from "../../means/_requests";
import DriverAPI from "../../drivers/_requests";
import { Provider } from "../../providers/_models";
import { Referent } from "../../referents/_models";
import { Mean } from "../../means/_models";
import { Driver } from "../../drivers/_models";
import ServiceFormBuilder, { generateEntityForm } from '../../services/builders/ServiceFormBuilder';
import { ExportPDFButton, RideDocument } from '../exportAsPdf';


const isObject = (obj: any): boolean => (obj !== null || obj !== undefined) && ((typeof obj === 'object'));
const isArray = (obj: any): boolean => Array.isArray(obj) || obj?.length > 0;

const getObject = (obj: any): any => {
    if (!isObject(obj)) {
        return obj;
    }
    return Array.isArray(obj) ? obj[0] : obj;
}

const entityFill = async (entity: Ride) => {
    if (!isObject(entity.provider) || isArray(entity.provider)) {
        const providerID = getObject((isArray(entity.provider)) ? entity.provider[0] : entity.provider);
        if (providerID) {
            const providerResponse = await ProviderAPI.get(providerID as number, {});
            entity.provider = providerResponse?.success ? providerResponse?.data : entity.provider;
        }
    }
    if (!isObject(entity.referent) || isArray(entity.referent)) {
        const referentID = getObject((isArray(entity.referent)) ? entity.referent[0] : entity.referent);
        if (referentID) {
            const referentResponse = await ReferentAPI.get(referentID as number, {});
            entity.referent = referentResponse?.success ? referentResponse?.data : entity.referent;
        }
    }
    if (!isObject(entity.mean) || isArray(entity.mean)) {
        const meanID = getObject((isArray(entity.mean)) ? entity.mean[0] : entity.mean);
        if (meanID) {
            const meanResponse = await MeanAPI.get(meanID as number, {});
            entity.mean = meanResponse?.success ? meanResponse?.data : entity.mean;
        }
    }
    if (!isObject(entity.driver) || isArray(entity.driver)) {
        const driverID = getObject((isArray(entity.driver)) ? entity.driver[0] : entity.driver);
        if (driverID) {
            const driverResponse = await DriverAPI.get(driverID as number, {});
            entity.driver = driverResponse?.success ? driverResponse?.data : entity.driver;
        }
    }
    return entity;
}

export const generateFormFields = async () => {
    const responseProvider = await ProviderAPI.getAllNoResourceNoPagination({});
    const responseReferent = await ReferentAPI.getAllNoResourceNoPagination({});
    const responseMean = await MeanAPI.getAllNoResourceNoPagination({});
    const responseDriver = await DriverAPI.getAllNoResourceNoPagination({});

    let providers: Array<Provider> = [];
    if (responseProvider?.success) {
        providers = responseProvider.data as Array<Provider>;
    }

    let referents: Array<Referent> = [];
    if (responseReferent?.success) {
        referents = responseReferent.data as Array<Referent>;
    }

    const referentsByProviderId = async (providerId: string | number) => {
        if (!providerId) {
            return []
        };
        const res = await ReferentAPI.getAllNoPagination({
            filters: {
                filterOn: "providers",
                field: "id",
                operator: "eq",
                value: providerId
            }
        });
        if (res?.success) {
            return res?.data?.map((referent: Referent) => {
                let label = `${referent.name ?? ""} ${referent.surname ?? ""}`;
                if (!referent.name && !referent.surname) {
                    label = referent.email ?? "NESSUNA EMAIL";
                }
                return {
                    label,
                    value: referent.id
                }
            });
        }
        return [];
    };

    let means: Array<Mean> = [];
    if (responseMean?.success) {
        means = responseMean.data as Array<Mean>;
    }

    let drivers: Array<Driver> = [];
    if (responseDriver?.success) {
        drivers = responseDriver.data as Array<Driver>;
    }


    const formFields: FormFields<Ride> = {
        title0: {
            type: "titleView",
            title: "Dettagli Corsa",
            icon: <i className="fa-solid fa-circle-play"></i>,
            columnNo: 0,
        },
        status: {
            type: "select",
            key: "status",
            label: "Stato della Corsa",
            isMulti: false,
            options: [
                { label: 'Da effettuare', value: 'TO_START' },
                { label: 'In Corso', value: 'IN_PROGRESS' },
                { label: 'Terminato', value: 'FINISHED' },
                { label: 'Cancellato', value: 'CANCELED' }
            ],
            validation: Yup.mixed().required('Lo Stato della Corsa è obbligatorio'),
            columnNo: 0,
        },
        is_taken_by_driver: {
            attributes: {
                disabled: true
            },
            type: "checkbox",

            key: "is_taken_by_driver",
            label: "Corsa presa in carico dall'autista",
            columnNo: 0,
        },
        start_city: {
            type: "text",
            key: "itinerary",
            label: "Itinerario",
            validation: Yup.string()
                .max(512, 'L\'Itinerario deve essere di massimo 50 caratteri')
                .required('L\'Itinerario è obbligatorio'),
            columnNo: 0,
        },
        start_place: {
            type: "text",
            key: "start_place",
            label: "Luogo di partenza",
            validation: Yup.string()
                .max(50, 'Il Luogo deve essere di massimo 50 caratteri'),
            columnNo: 0,
        },
        start_appointment_date: {
            type: "datetime_picker",
            key: "start_appointment_date",
            label: "Data e Ora ritrovo",
            validation: Yup.array().of(Yup.object().shape({
                from: Yup.string().required('La Data e Ora ritrovo è obbligatoria'),
                to: Yup.string().required('La Data e Ora ritrovo è obbligatoria'),
            })).required('La Data e Ora ritrovo è obbligatoria'),
            columnNo: 0,
        },
        start_departure_date: {
            type: "datetime_picker",
            key: "start_departure_date",
            label: "Data e Ora partenza",
            linkedTo: "start_appointment_date",
            validation: Yup.array().of(Yup.object().shape({
                from: Yup.string().required('La Data e Ora partenza è obbligatoria'),
                to: Yup.string().required('La Data e Ora partenza è obbligatoria'),
            })).required('La Data e Ora partenza è obbligatoria'),
            columnNo: 0,
        },
        end_place: {
            type: "text",
            key: "end_place",
            label: "Luogo di arrivo",
            validation: Yup.string()
                .max(50, 'Il Luogo deve essere di massimo 50 caratteri'),
            columnNo: 0,
        },
        end_departure_date: {
            type: "datetime_picker",
            key: "end_departure_date",
            label: "Data e ora di fine corsa",
            validation: Yup.array().of(Yup.object().shape({
                from: Yup.string().required('La Data di Arrico è obbligatoria'),
                to: Yup.string().required('La Data di Arrivo è obbligatoria'),
            })).nullable(),
            columnNo: 0,
        },
        notes: {
            type: "wysiwyg",
            key: "ride_note",
            label: "Descrizione itinerario",
            validation: Yup.string(),
            columnNo: 0,
        },
        title2: {
            type: "titleView",
            title: "Info",
            icon: <i className="fa-solid fa-bus"></i>,
            columnNo: 1,
        },
        provider: {
            type: "select",
            key: "provider",
            label: "Fornitore",
            isMulti: false,
            options: providers.map(provider => ({ value: (provider.id?.toString()), label: `${provider.name ?? ""} ${provider.surname ?? ""}` })),
            columnNo: 1,
        },
        referent: {
            type: "select",
            key: "referent",
            label: "Referente",
            isMulti: false,
            //FIX: dependsOn doesn't work | it should fetch the options only when the provider is selected
            //optionsFetcher: async (ride: any) => {
            //    let providerID = ride?.provider?.id ?? ride?.provider;
            //    if (Array.isArray(ride?.provider)) {
            //        providerID = ride.provider?.length > 1 ? ride.provider[0] : null
            //    }
            //    return await referentsByProviderId(providerID)
            //},
            //dependsOn: "provider",
            options: referents.map(referent => ({ value: (referent.id?.toString()), label: `${referent.email} ${referent.name ?? ""} ${referent.surname ?? ""}` })),
            columnNo: 1,
        },
        passengers: {
            type: "text",
            key: "passengers",
            label: "Numero passeggeri",
            validation: Yup.number().integer().positive("Il numero deve essere maggiore di 0").nullable().typeError("Inserisci un numero valido"),
            columnNo: 1,
        },
        kilometers: {
            type: "text",
            key: "kilometers",
            label: "Chilometri",
            validation: Yup.number().integer().positive("Il numero deve essere maggiore di 0").nullable().typeError("Inserisci un numero valido"),
            columnNo: 1,
        },
        mean: {
            type: "select",
            key: "mean",
            label: "Mezzo",
            isMulti: false,
            options: means.map((mean: Mean) => ({ value: (mean.id?.toString()), label: `${mean?.code ?? ""} ${mean?.license_plate ?? ""}` })),
            columnNo: 1,
        },
        driver: {
            type: "select",
            key: "driver",
            label: "Autista",
            isMulti: false,
            options: drivers.map(driver => ({ value: (driver.id?.toString()), label: `${driver.name ?? ""} ${driver.surname ?? ""}` })),
            columnNo: 1,
        },
        is_assigned: {
            type: "checkbox",
            key: "is_assigned",
            label: "Assegna la corsa all'autista",
            columnNo: 1,
        },
        effort_hours: {
            type: "text",
            key: "effort_hours",
            label: "Ore di Impiego",
            validation: Yup.number().integer().positive("Il numero deve essere maggiore di 0").nullable().typeError("Inserisci un numero valido"),
            columnNo: 1,
        },
        driving_hours: {
            type: "text",
            key: "driving_hours",
            label: "Ore di Guida",
            validation: Yup.number().integer().positive("Il numero deve essere maggiore di 0").nullable().typeError("Inserisci un numero valido"),
            columnNo: 1,
        },
        title3: {
            type: "titleView",
            title: "Spese e Pedaggi",
            icon: <i className="fa-solid fa-wallet"></i>,
            columnNo: 2,
        },
        ztl_price: {
            type: "text",
            isCurrency: true,
            key: "ztl_price",
            label: "ZTL",
            validation: Yup.number().positive("Inserisci un valore positivo").nullable().typeError("Inserisci un valore valido"),
            columnNo: 2,
        },
        tolls_price: {
            type: "text",
            isCurrency: true,
            key: "tolls_price",
            label: "Pedaggi",
            validation: Yup.number().positive("Inserisci un valore positivo").nullable().typeError("Inserisci un valore valido"),
            columnNo: 2,
        },
        other_price: {
            type: "text",
            isCurrency: true,
            key: "other_price",
            label: "Altro",
            validation: Yup.number().positive("Inserisci un valore positivo").nullable().typeError("Inserisci un valore valido"),
            columnNo: 2,
        },
        title4: {
            type: "titleView",
            title: "Note",
            icon: <i className="fa-solid fa-clipboard"></i>,
            classes: "mt-7",
            columnNo: 2,
        },
        notes_for_driver: {
            type: "wysiwyg",
            key: "notes_for_driver",
            label: "Note per l'Autista",
            validation: Yup.string().nullable()
                .max(500, 'Le Note per l\'Autista devono essere di massimo 500 caratteri'),
            columnNo: 2,
        },
        driver_notes: {
            type: "wysiwyg",
            key: "driver_notes",
            label: "Note dall'Autista",
            validation: Yup.string().nullable()
                .max(500, 'Le Note per dall\'Autista devono essere di massimo 500 caratteri'),
            columnNo: 2,
        },
        exportPDF: {
            type: "custom",
            key: "",
            label: "Esporta come pdf",
            // HACK: manage empty key to get entire entity instead of single field
            render: (formik: any, entity?: Ride, field?: FormField<Ride>) => ((field && entity) ?
                <ExportPDFButton
                    entity={
                        {
                            ...formik.getFieldProps(field?.key?.slice(0, -1),)?.value,
                            service: { ...entity }
                        }
                    }
                    Document={RideDocument}
                    entityFill={entityFill}
                /> : <></>

            ),
            columnNo: 2,
        },
    };
    return formFields;
}


const RideFormBuilder = async () =>
    await ServiceFormBuilder({ defaultActiveTab: 1 });

export default RideFormBuilder;
